/**
 * @component ry-theme-toggle
 * @category  display
 * @summary   Single-button light/dark mode toggle (ry-ui-only). Renders a sun/moon icon button; clicking toggles `data-ry-mode` on `<html>` and persists the choice to localStorage. First click after a fresh visit flips relative to OS preference (no stored value); subsequent clicks are a pure binary flip. Shares the same storage key as `<ry-theme-panel>` so the two stay in sync.
 *
 * @property  mode   'light' | 'dark'   Live current mode. Setting writes `<html data-ry-mode="...">`, applies `color-scheme`, persists to localStorage, and updates the button icon.
 *
 * @method    toggle()  Flip mode (light → dark or dark → light). Fires `ry:mode-change` with `{mode}`.
 *
 * @event ry:mode-change
 *        detail: { mode: 'light' | 'dark' }
 *        fires:  after every `toggle()` call (including the user clicking the rendered button).
 *
 * @keyboard  None on the host — the inner `<button>` gets native Enter/Space activation via standard button semantics.
 *
 * @content   None — `<ry-theme-toggle>` renders its own button. To override the icon, author the inner markup yourself (the wrapper bails when innerHTML is non-empty at mount).
 *
 * @dom
 *   ry-theme-toggle
 *     > button.ry-btn.ry-btn--ghost   [data-ry-target="button"]   aria-label="Toggle color mode"
 *       > {☀ sun icon when light, ☾ moon when dark}
 *
 * @gotcha  The component WRITES to `<html data-ry-mode="...">` and `<html style="color-scheme: ...">` on every toggle. If your page already manages the mode attribute elsewhere, expect them to overwrite each other.
 *
 * @gotcha  Storage key is `"ry-theme-panel"` — shared with `<ry-theme-panel>`. JSON shape: `{"mode":"light"|"dark"}`. Don't write to it from non-ry-ui code.
 *
 * @gotcha  Initial mode resolution order: (1) localStorage stored mode → (2) existing `<html data-ry-mode>` → (3) OS preference via `prefers-color-scheme`. The component does NOT call `#apply()` at setup — only on user toggle. If you need the document mode to match storage at boot, call `el.mode = el.mode` after mount, OR set `<html data-ry-mode>` server-side.
 *
 * @gotcha  Authored inner markup is preserved — the wrapper checks `!this.innerHTML.trim()` before rendering its default button. Place your own button and the wrapper won't replace it.
 *
 * @gotcha  Setting `el.mode` to an invalid value (anything other than `"light"` / `"dark"`) is a silent no-op — no validation error, no warning.
 *
 * @example basic
 * ```html
 * <ry-theme-toggle></ry-theme-toggle>
 * ```
 *
 * @example with-custom-icon
 * ```html
 * <ry-theme-toggle>
 *   <button data-ry-target="button" aria-label="Toggle theme">🌓</button>
 * </ry-theme-toggle>
 * ```
 *
 * @tutorial mount-renders-default-button
 * ```js
 * const el = mount('<ry-theme-toggle></ry-theme-toggle>');
 * const btn = el.querySelector('[data-ry-target="button"]');
 * await expectEqual(!!btn, true, 'default button rendered');
 * await expectAttr(btn, 'aria-label', 'Toggle color mode');
 * ```
 *
 * @tutorial preserves-authored-button
 * ```js
 * // Wrong assumption: the wrapper always replaces innerHTML. Authored
 * // markup is preserved if it's non-empty at mount.
 * const el = mount('<ry-theme-toggle><button data-ry-target="button">custom</button></ry-theme-toggle>');
 * const btn = el.querySelector('[data-ry-target="button"]');
 * await expectEqual(btn.textContent, 'custom', 'authored button preserved');
 * ```
 *
 * @tutorial click-toggles-and-fires-mode-change
 * ```js
 * // Reset storage to ensure deterministic test state
 * try { localStorage.removeItem('ry-theme-panel'); } catch (e) {}
 * document.documentElement.removeAttribute('data-ry-mode');
 *
 * const el = mount('<ry-theme-toggle></ry-theme-toggle>');
 * const fired = [];
 * el.addEventListener('ry:mode-change', (e) => fired.push(e.detail.mode));
 * el.click();
 * await expectEqual(fired.length, 1, 'one ry:mode-change fired');
 * await expectAttr(document.documentElement, 'data-ry-mode', el.mode, 'document.documentElement updated to new mode');
 * try { localStorage.removeItem('ry-theme-panel'); } catch (e) {}
 * ```
 *
 * @tutorial toggle-persists-to-localstorage
 * ```js
 * try { localStorage.removeItem('ry-theme-panel'); } catch (e) {}
 * const el = mount('<ry-theme-toggle></ry-theme-toggle>');
 * el.mode = 'dark';
 * try {
 *   const stored = JSON.parse(localStorage.getItem('ry-theme-panel') || '{}');
 *   await expectEqual(stored.mode, 'dark', 'mode persisted under shared key');
 * } finally {
 *   try { localStorage.removeItem('ry-theme-panel'); } catch (e) {}
 * }
 * ```
 *
 * @tutorial storage-key-shared-with-theme-panel
 * ```js
 * // The storage key is the literal string "ry-theme-panel" — set up
 * // <ry-theme-panel> and <ry-theme-toggle> with the same shared key so
 * // they reflect each other.
 * try { localStorage.setItem('ry-theme-panel', JSON.stringify({ mode: 'dark' })); } catch (e) {}
 * const el = mount('<ry-theme-toggle></ry-theme-toggle>');
 * await expectEqual(el.mode, 'dark', 'restored from shared storage key');
 * try { localStorage.removeItem('ry-theme-panel'); } catch (e) {}
 * ```
 *
 * @tutorial invalid-mode-setter-is-silent-noop
 * ```js
 * try { localStorage.removeItem('ry-theme-panel'); } catch (e) {}
 * const el = mount('<ry-theme-toggle></ry-theme-toggle>');
 * const before = el.mode;
 * el.mode = 'auto';  // invalid value
 * await expectEqual(el.mode, before, 'invalid value rejected silently');
 * el.mode = 'pink';
 * await expectEqual(el.mode, before);
 * try { localStorage.removeItem('ry-theme-panel'); } catch (e) {}
 * ```
 *
 * @see ry-theme-panel
 */

import { RyElement } from '../core/ry-element.js';
import { getIcon } from '../core/ry-icons.js';

interface ModeChangeDetail {
  mode: 'light' | 'dark';
}

const STORAGE_KEY = 'ry-theme-panel';

type Mode = 'light' | 'dark';

export class RyThemeToggle extends RyElement {
  #mode: Mode = 'light';

  setup(): void {
    this.#mode = this.#resolveInitialMode();

    if (!this.innerHTML.trim()) {
      this.innerHTML = `<button data-ry-target="button" class="ry-btn ry-btn--ghost ry-btn--icon" aria-label="Toggle color mode">${this.#icon()}</button>`;
    }

    this.on(this, 'click', () => this.toggle());
  }

  #resolveInitialMode(): Mode {
    const stored = this.#loadStorage();
    if (stored === 'light' || stored === 'dark') return stored;

    const docMode = document.documentElement.dataset.ryMode;
    if (docMode === 'light' || docMode === 'dark') return docMode;

    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }

  #icon(): string {
    return getIcon(this.#mode === 'dark' ? 'moon' : 'sun');
  }

  toggle(): void {
    this.mode = this.#mode === 'light' ? 'dark' : 'light';
    this.emit<ModeChangeDetail>('mode-change', { mode: this.#mode });
  }

  #apply(): void {
    const root = document.documentElement;
    root.dataset.ryMode = this.#mode;
    root.style.colorScheme = this.#mode;
  }

  #save(): void {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ mode: this.#mode }));
    } catch { /* storage unavailable */ }
  }

  #loadStorage(): Mode | null {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw) as { mode?: string };
      return parsed.mode === 'light' || parsed.mode === 'dark' ? parsed.mode : null;
    } catch { return null; }
  }

  get mode(): Mode { return this.#mode; }
  set mode(v: Mode) {
    if (v !== 'light' && v !== 'dark') return;
    this.#mode = v;
    this.#apply();
    this.#save();
    const btn = this.$('[data-ry-target="button"]');
    if (btn) btn.innerHTML = this.#icon();
  }
}

customElements.define('ry-theme-toggle', RyThemeToggle);
